import type { ProductionScheduleItem } from "@/types/productionBrief.type";

/**
 * Shoot-day timing for the production schedule.
 *
 * The brief stores `startTime` ("HH:mm") and `durationMinutes` per row and
 * derives the end from the two — backend issue #69. It used to be packed into
 * the row's free-text `time` as "09:00–09:45 · 45 min" and read back out with a
 * regex, because there was nowhere else to put it; there is now, so the packed
 * string is gone. Nothing here writes `time`.
 *
 * `time` survives as exactly what it was before any of this: a free-text note
 * somebody typed ("15 May, client availability", "after lunch"). Those rows are
 * never rewritten, never reflowed, and never parsed for meaning — the one
 * exception being a note that is nothing but a clock, which is offered up as a
 * start so it can be promoted to the structured pair on first edit.
 *
 * NOTE this is shoot-day minutes — how long the crew spends on a task. A shot's
 * `duration` is how long it is on screen. The two never meet.
 */

const CLOCK = /^([01]\d|2[0-3]):([0-5]\d)$/;

const MINUTES_IN_DAY = 1440;

export type ParsedScheduleTiming = {
  /** "" when the row has no usable start. */
  start: string;
  /** Derived from start + duration; "" when either is missing. */
  end: string;
  durationMinutes: number | null;
  /** The row carries free text that is not a clock, so it cannot be reflowed. */
  legacy: boolean;
  /** That free text, verbatim. "" when there is none. */
  legacyNote: string;
};

function clockToMinutes(value: string): number | null {
  const match = CLOCK.exec(value.trim());
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

export function formatClock(totalMinutes: number): string {
  const inDay = ((Math.round(totalMinutes) % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  return `${String(Math.floor(inDay / 60)).padStart(2, "0")}:${String(inDay % 60).padStart(2, "0")}`;
}

function validDuration(value: number | null | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  const whole = Math.round(value);
  return whole > 0 && whole <= MINUTES_IN_DAY ? whole : null;
}

export function parseScheduleTiming(item: ProductionScheduleItem): ParsedScheduleTiming {
  const note = (item.time ?? "").trim();
  // A bare clock in the legacy field is a start somebody meant, so it is offered
  // as one. It is promoted into `startTime` the moment the row is edited.
  const legacyClock = clockToMinutes(note) !== null ? note : "";
  const start = (item.startTime ?? "").trim() || legacyClock;
  const durationMinutes = validDuration(item.durationMinutes);
  const startMinutes = clockToMinutes(start);
  return {
    start: startMinutes === null ? "" : start,
    end:
      startMinutes !== null && durationMinutes !== null
        ? formatClock(startMinutes + durationMinutes)
        : "",
    durationMinutes,
    legacy: note.length > 0 && !legacyClock,
    legacyNote: legacyClock ? "" : note,
  };
}

function firstTimedStart(items: ProductionScheduleItem[]): string | null {
  for (const item of items) {
    const timing = parseScheduleTiming(item);
    if (timing.start && timing.durationMinutes) return timing.start;
  }
  return null;
}

/** Structured timing on a row, with any promoted legacy clock cleared off it. */
function withTiming(
  item: ProductionScheduleItem,
  start: string,
  durationMinutes: number | null,
): ProductionScheduleItem {
  const next: ProductionScheduleItem = { ...item, startTime: start };
  if (durationMinutes === null) delete next.durationMinutes;
  else next.durationMinutes = durationMinutes;
  // The clock has moved into `startTime`; leaving it behind would show the same
  // time twice and make the row look like it had a note it never had.
  if (parseScheduleTiming(item).legacy === false && (item.time ?? "").trim()) {
    delete next.time;
  }
  return next;
}

/**
 * Lay every timed row back-to-back from one stable window start.
 *
 * Free-text/legacy rows are deliberately skipped rather than rewritten: they may
 * contain a date, call-time note, or other production detail that cannot be
 * represented by the structured clock controls.
 */
function reflowTimedRows(
  items: ProductionScheduleItem[],
  anchorStart: string,
): ProductionScheduleItem[] {
  const next = items.map((item) => ({ ...item }));
  let cursor = clockToMinutes(anchorStart);
  if (cursor === null) return next;

  for (let index = 0; index < next.length; index += 1) {
    const row = next[index]!;
    const duration = validDuration(row.durationMinutes);
    if (duration === null) continue;
    next[index] = withTiming(row, formatClock(cursor), duration);
    cursor += duration;
  }
  return next;
}

export function updateScheduleTiming(
  items: ProductionScheduleItem[],
  index: number,
  start: string,
  durationMinutes: number | null,
): ProductionScheduleItem[] {
  // Invalid/blank structured input must never replace a legacy note such as
  // "15 May" with an empty string. The caller can still edit that note through
  // its dedicated free-text field.
  if (clockToMinutes(start) === null) return items.map((item) => ({ ...item }));

  const originalWindowStart = firstTimedStart(items);
  const next = items.map((item, itemIndex) =>
    itemIndex === index ? withTiming(item, start, validDuration(durationMinutes)) : { ...item },
  );
  const firstTimedIndex = next.findIndex((item) => {
    const timing = parseScheduleTiming(item);
    return Boolean(timing.start && timing.durationMinutes);
  });

  // A newly structured first row establishes the window. Edits elsewhere keep
  // the original start and reflow by list order, so gaps and overlaps cannot be
  // introduced by a duration or start change.
  const anchorStart = firstTimedIndex === index ? start : (originalWindowStart ?? start);
  return reflowTimedRows(next, anchorStart);
}

/**
 * Clear only a structured clock value. Free-text legacy notes are protected so
 * an empty native time-input event cannot erase information it never displayed.
 */
export function clearScheduleTiming(
  items: ProductionScheduleItem[],
  index: number,
): ProductionScheduleItem[] {
  if (index < 0 || index >= items.length) return items;
  const current = parseScheduleTiming(items[index]!);
  if (!current.start) return items.map((item) => ({ ...item }));

  const windowStart = firstTimedStart(items);
  const next = items.map((item, itemIndex) => {
    if (itemIndex !== index) return { ...item };
    const cleared = { ...item };
    delete cleared.startTime;
    delete cleared.durationMinutes;
    // A promoted clock lived in `time`; clearing the timing clears that too,
    // otherwise the row would silently revert to its old packed value.
    if (!current.legacy) delete cleared.time;
    return cleared;
  });
  return windowStart ? reflowTimedRows(next, windowStart) : next;
}

export function moveScheduleItem(
  items: ProductionScheduleItem[],
  from: number,
  to: number,
): ProductionScheduleItem[] {
  if (to < 0 || to >= items.length || from === to) return items;
  const windowStart = firstTimedStart(items);
  const next = items.map((item) => ({ ...item }));
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return windowStart ? reflowTimedRows(next, windowStart) : next;
}

export function removeScheduleItem(
  items: ProductionScheduleItem[],
  index: number,
): ProductionScheduleItem[] {
  if (index < 0 || index >= items.length) return items;
  const windowStart = firstTimedStart(items);
  const next = items.filter((_, itemIndex) => itemIndex !== index).map((item) => ({ ...item }));
  return windowStart ? reflowTimedRows(next, windowStart) : next;
}

export function scheduleWindow(
  items: ProductionScheduleItem[] | undefined,
): { start: string; end: string; durationMinutes: number } | null {
  const timedRows = (items ?? [])
    .map((item) => parseScheduleTiming(item))
    .filter((row) => row.start && row.end && row.durationMinutes);
  const first = timedRows[0];
  if (!first) return null;
  const last = timedRows[timedRows.length - 1] ?? first;
  const durationMinutes = timedRows.reduce((total, row) => total + (row.durationMinutes ?? 0), 0);
  return { start: first.start, end: last.end, durationMinutes };
}

/** A stable id for a new row, so reorder and diff never rely on array index. */
export function newScheduleRowId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `row-${Math.random().toString(36).slice(2, 11)}`;
}

export function blankScheduleItem(): ProductionScheduleItem {
  return { id: newScheduleRowId(), activity: "", owner: "" };
}

/**
 * Backfill ids on rows written before they existed, so the editor can key off
 * `id` alone. Pure: returns new rows, never mutates the draft in place.
 */
export function withScheduleRowIds(
  items: ProductionScheduleItem[] | undefined,
): ProductionScheduleItem[] {
  return (items ?? []).map((item) => (item.id ? { ...item } : { ...item, id: newScheduleRowId() }));
}
